import React from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import { chartOptions } from './config'

// Thumbnail charts should not exaggerate small prediction deltas.
const MIN_THUMBNAIL_AXIS_RANGE_PCT = 0.05;
const THUMBNAIL_AXIS_PADDING_PCT = 0.15;

function collectYValues(series) {
  const values = [];
  if (!Array.isArray(series)) return values;

  for (const point of series) {
    let y;

    if (Array.isArray(point)) {
      y = point[1];
    } else if (point && typeof point === 'object') {
      y = point.y;
    } else {
      y = point;
    }

    const num = typeof y === 'string' ? Number(y) : y;
    if (Number.isFinite(num)) values.push(num);
  }

  return values;
}

function computeThumbnailYAxisExtremes(close, prediction) {
  const ys = [...collectYValues(close), ...collectYValues(prediction)];
  if (ys.length === 0) return null;

  let minY = Number.POSITIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;
  for (const y of ys) {
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }

  if (!Number.isFinite(minY) || !Number.isFinite(maxY)) return null;

  const mid = (minY + maxY) / 2;
  const range = maxY - minY;
  const minRangeAbs = Math.max(Math.abs(mid) * MIN_THUMBNAIL_AXIS_RANGE_PCT, 1e-6);
  const paddedRange = range * (1 + THUMBNAIL_AXIS_PADDING_PCT);
  const finalRange = Math.max(minRangeAbs, paddedRange);
  const half = finalRange / 2;

  return {
    min: mid - half,
    max: mid + half,
  };
}

const Chart = ({ close, prediction }) => {
  const yAxisExtremes = React.useMemo(
    () => computeThumbnailYAxisExtremes(close, prediction),
    [close, prediction],
  );

  const options = React.useMemo(() =>({
    ...chartOptions,
    tooltip: {
      pointFormat:
        '<span style="font-size:10px; color:{series.color}">{series.name}:</span> <span style="font-size:10px; font-weight: bold">{point.y}</span>',
      valueDecimals: 2,
      split: true,
      xDateFormat: '%b %e, %Y'
    },
    yAxis: chartOptions.yAxis?.map((axis, idx) => {
      if (idx !== 0 || !yAxisExtremes) return axis;
      return {
        ...axis,
        ...yAxisExtremes,
      };
    }) ?? chartOptions.yAxis,
    series: [
      {
        name: "Close",
        type: "area",
        threshold: null,
        yAxis: 0,
        xAxis: 0,
        data: close,
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, "rgba(68, 188, 246, 1)"],
            [1, Highcharts.color(Highcharts.getOptions().colors[0]).setOpacity(0).get("rgba")],
          ],
        },
      },
      {
        name: "Prediction",
        yAxis: 0,
        xAxis: 0,
        type: "area",
        threshold: null,
        data: prediction,
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, "rgba(255, 18, 24, 1)"],
            [1, Highcharts.color(Highcharts.getOptions().colors[1]).setOpacity(0).get("rgba")],
          ],
        },
      },
    ],
  }), [close, prediction, yAxisExtremes]);

  return (
    <HighchartsReact highcharts={Highcharts} options={options} />
  )
}

export default Chart;
